import { VERDICT_KEYS, scopePartial, type CreativeFormat, type CreativeSummary, type CreativeVerdict, type CreativesResponse, type Measure, type ObservedMetrics, type VerdictKey } from './contract'
import type { ConfidenceBand, ConfidenceThresholds } from './confidence'
import { DEFAULT_CONFIDENCE_THRESHOLDS, reliabilityOf } from './confidence'
import type { Derived } from './metrics'
import { aggregateUnder, derive, spendShares, sumMoney, type SpendShare } from './metrics'

/**
 * A creative with everything the screen derived from it, computed once per response. Its
 * `verdict` is the served one, carried through as decoded.
 */
export type Row = CreativeSummary & {
  derived: Derived
  band: ConfidenceBand
  /**
   * The row's percent of the segment's spend, allocated so the reported rows sum to 100.
   * `pct` is null when the row's spend is missing or the segment total is zero: unknown,
   * never 0%.
   */
  share: SpendShare
}

/**
 * Everything the screen derives from a response, computed once for every creative in it.
 *
 * Share is left at zero here and allocated by `withShare` over the segment that is about
 * to be shown, because the column has to sum to 100 in front of the reader: a share
 * allocated over the whole response would leave a filtered table adding up to less than
 * everything with nothing on screen accounting for the rest.
 */
export function buildRows(
  creatives: CreativeSummary[],
  thresholds: ConfidenceThresholds = DEFAULT_CONFIDENCE_THRESHOLDS,
): Row[] {
  return creatives.map((creative) => {
    const derived = derive(creative.metrics)
    return {
      ...creative,
      derived,
      band: reliabilityOf(creative.metrics.conversions ?? 0, thresholds),
      share: { pct: null, partial: false },
    }
  })
}

/**
 * The rows that ever delivered. A «Не запущен» row adds nothing to any aggregate of the
 * current window (PRD 0006 section 11, GRO-187): every total, share and pool reads through
 * this. The comparison's rows carry no verdict and are never filtered.
 */
export function launched<T extends { verdict: CreativeVerdict | null }>(rows: T[]): T[] {
  return rows.filter((row) => row.verdict?.key !== 'notStarted')
}

/**
 * Whether the screen must treat the figures as partial (GRO-195 D8): `scopePartial`, or no
 * launched creative while placeholder ads delivered. Those ads are settled and leave
 * `scopePartial` false, but a list without a launched creative must not read as "nothing ran".
 */
export function partialScopeOf(response: Pick<CreativesResponse, 'scope' | 'creatives'>): boolean {
  return scopePartial(response.scope) || (
    launched(response.creatives).length === 0
    && response.scope.unplacedReasons.some((g) => g.reason === 'placeholder_image' && !g.excluded)
  )
}

/**
 * Allocate spend share over exactly these rows, so the column sums to 100 over them. A
 * «Не запущен» row takes no share.
 */
export function withShare(rows: Row[]): Row[] {
  const counted = launched(rows)
  const shares = new Map(spendShares(counted).map((share, index) => [counted[index], share] as const))
  return rows.map((row) => ({ ...row, share: shares.get(row) ?? { pct: null, partial: false } }))
}

export type SortKey =
  | 'name'
  | 'accounts'
  | 'format'
  | 'hypothesis'
  | 'audience'
  | 'technique'
  | 'spend'
  | 'share'
  | 'hook'
  | 'hold'
  | 'ctr'
  | 'purch'
  | 'roas'
  | 'adj'
  | 'cpa'
  | 'aov'
  | 'cpc'
  | 'cpm'
  | 'activeDays'
  | 'ads'
  | 'verdict'

/**
 * The bucket id for creatives whose attribute is null on an axis, on either attribute axis.
 *
 * Defined beside `segment`, the one function that interprets it: the value is a statement
 * about `CreativeSummary`'s nullable attributes, which `segment` has to read whether or not
 * a comparison screen exists. No dimension id collides with it, because ids are slugs from
 * the naming dictionaries and never start with two underscores.
 */
export const UNASSIGNED = '__unassigned'

export interface SegmentFilters {
  hypothesisId?: string | null
  techniqueId?: string | null
  messageId?: string | null
  offerId?: string | null
  format?: CreativeFormat | null
}

/** The value axes `segment` narrows by, each with the unassigned sentinel. */
const SEGMENT_AXES = ['hypothesisId', 'techniqueId', 'messageId', 'offerId'] as const

/**
 * The client-side narrowing: hypothesis, technique, message, offer and media type.
 *
 * Account and audience are not here. They are scopes the server aggregates over, because
 * after merging by asset hash the client cannot tell which spend belonged to which
 * audience, and a filter that selected whole creatives would leave their figures
 * describing every audience while the filter claimed one.
 *
 * The unassigned sentinel is the one filter value that matches a null attribute: set on
 * `hypothesisId` it selects creatives carrying no hypothesis, set on `techniqueId`,
 * `messageId` or `offerId` the ones carrying none of that axis. A plain `null` filter
 * stays no narrowing at all.
 */
/** The attributes the segment filters read: a current row and a prior creative both carry them. */
export interface Segmentable {
  hypothesisId: string | null
  techniqueId: string | null
  messageId: string | null
  offerId: string | null
  format: CreativeFormat
}

export function segment<T extends Segmentable>(rows: T[], filters: SegmentFilters): T[] {
  return rows.filter((row) => {
    for (const axis of SEGMENT_AXES) {
      const wanted = filters[axis]
      if (wanted == null) continue
      if (wanted === UNASSIGNED) {
        if (row[axis] !== null) return false
      } else if (row[axis] !== wanted) return false
    }
    if (filters.format != null && row.format !== filters.format) return false
    return true
  })
}

export interface Floor {
  spend: number
  conversions: number
}

/**
 * The reader's own minimums: a display filter and nothing more.
 *
 * Hidden rows are counted and returned, because the tiles and the thin banner
 * keep describing the whole segment. The floor never reaches the reliability
 * thresholds: it hides rows from a list, it does not decide what can be ranked.
 */
export function floor(rows: Row[], min: Floor): { shown: Row[]; hidden: Row[] } {
  const shown: Row[] = []
  const hidden: Row[] = []
  for (const row of rows) {
    const spend = row.metrics.spend ?? 0
    const conversions = row.metrics.conversions ?? 0
    if (spend >= min.spend && conversions >= min.conversions) shown.push(row)
    else hidden.push(row)
  }
  return { shown, hidden }
}

/**
 * The verdict filter: a display filter, like the floor.
 *
 * It hides rows from the list and nothing more. The tiles, the share column and the thin
 * banner keep describing the segment, because a verdict is a judgement against the 90-day
 * average and "show me the winners" is the same kind of question as "show me the big
 * spenders". The benchmarks cannot follow it at all: they come from the server over the
 * account scope (GRO-64 design, D4).
 */
export function byVerdict(rows: Row[], verdict: VerdictKey | null): Row[] {
  // A key the server no longer serves, from an older saved view, reads as no filter.
  if (verdict === null || !(VERDICT_KEYS as readonly string[]).includes(verdict)) return rows
  return rows.filter((row) => row.verdict?.key === verdict)
}

/**
 * The name search: a display filter, applied after the verdict and on the same terms, so
 * the tiles keep describing the segment (GRO-165). It reads the name the row shows and
 * nothing else; a name is a buyer's label, never identity.
 */
export function byName(rows: Row[], query: string): Row[] {
  const needle = query.trim().toLocaleLowerCase()
  if (needle === '') return rows
  return rows.filter((row) => row.displayName.toLocaleLowerCase().includes(needle))
}

/**
 * Scale first, Kill last: the order the two verdict Questions read down and up. «Данных мало»
 * sits above the two problem verdicts, because no data is not a judgement against the
 * creative; a null verdict sorts with it.
 */
export const VERDICT_ORDER: Record<VerdictKey, number> = {
  scale: 5,
  hold: 4,
  watch: 3,
  insufficient: 2,
  rework: 1,
  kill: 0,
  notStarted: -1,
}

/**
 * Below the ranking floor (PRD 0006 section 5): the served `n` under `directionAt`. A
 * stop-loss verdict never folds, a «Не запущен» row is no sample at all, and a null
 * verdict does not fold either.
 */
export function belowFloor(row: Pick<Row, 'verdict'>, thresholds: ConfidenceThresholds): boolean {
  if (row.verdict === null) return false
  return row.verdict.n < thresholds.directionAt && row.verdict.key !== 'kill' && row.verdict.key !== 'notStarted'
}

function numeric(row: Row, sort: SortKey): number | null {
  switch (sort) {
    case 'spend':
      return row.metrics.spend
    case 'share':
      return row.share.pct
    case 'purch':
      return row.metrics.conversions
    case 'hook':
      return row.derived.hookRate.value
    case 'hold':
      return row.derived.holdRate.value
    case 'ctr':
      return row.derived.ctr.value
    case 'roas':
      return row.derived.roas.value
    case 'adj':
      return row.adjustedRoas
    case 'cpa':
      return row.derived.cpa.value
    case 'aov':
      return row.derived.aov.value
    case 'cpc':
      return row.derived.cpc.value
    case 'cpm':
      return row.derived.cpm.value
    case 'activeDays':
      return row.activeDays
    case 'ads':
      return row.ads.length
    case 'accounts':
      return new Set(row.ads.map((ad) => ad.accountId)).size
    case 'verdict':
      return VERDICT_ORDER[row.verdict?.key ?? 'insufficient']
    default:
      return null
  }
}

/**
 * The text a sorted column shows for a row. The ids stand in when no labels are given; a
 * screen passes the labels, so the order follows what the reader sees in every language.
 */
export interface SortLabels {
  audience: (row: Row) => string
  hypothesis: (row: Row) => string | null
  technique: (row: Row) => string | null
}

const ID_LABELS: SortLabels = {
  audience: (row) => row.audienceIds.join(','),
  hypothesis: (row) => row.hypothesisId,
  technique: (row) => row.techniqueId,
}

function text(row: Row, sort: SortKey, labels: SortLabels): string | null {
  switch (sort) {
    case 'name':
      return row.displayName
    case 'format':
      // The wire value, not the label: `sortRows` receives no dictionary and no language, so
      // carousel, image, video is one fixed order the reader can rely on in every language.
      return row.format
    case 'hypothesis':
      return labels.hypothesis(row)
    case 'audience':
      // The cell's text, so the order follows the labels the column shows in every language
      // and the mixed rows sort together (GRO-175). An empty cell sorts last.
      return row.audienceIds.length === 0 ? null : labels.audience(row)
    case 'technique':
      return labels.technique(row)
    default:
      return null
  }
}

/**
 * Sort, with `null` last in both directions.
 *
 * A creative with no ROAS is not the worst performer and it is not the best one either;
 * it has no ROAS. Sinking it in both directions keeps the top of the list answering the
 * question the reader asked, ascending or descending, and keeps the reader from reading an
 * absent figure as a zero.
 */
export function sortRows(
  rows: Row[],
  sort: SortKey,
  dir: 'asc' | 'desc',
  labels: SortLabels = ID_LABELS,
): Row[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((left, right) => {
    const leftText = text(left, sort, labels)
    if (leftText !== null || text(right, sort, labels) !== null) {
      const rightText = text(right, sort, labels)
      if (leftText === null) return 1
      if (rightText === null) return -1
      const compared = leftText.localeCompare(rightText)
      if (compared !== 0) return sign * compared
      return left.id.localeCompare(right.id)
    }

    const leftValue = numeric(left, sort)
    const rightValue = numeric(right, sort)
    if (leftValue === null && rightValue === null) return left.id.localeCompare(right.id)
    if (leftValue === null) return 1
    if (rightValue === null) return -1
    if (leftValue === rightValue) return left.id.localeCompare(right.id)
    return sign * (leftValue - rightValue)
  })
}

/**
 * The figures a segment's metrics alone decide: what a prior creative carries as much as
 * a current row, so the previous period's totals come from the same arithmetic.
 */
export interface ObservedTotals {
  /**
   * The additive sums: a number when the segment reported the measure, `null` when it
   * has rows and none of them did - the no-value glyph's case, the same reading the
   * comparison's bucket rows give. Zero is an empty segment's total, never a silent
   * measure's.
   */
  spend: number | null
  conversions: number | null
  impressions: number | null
  conversionValue: number | null
  roas: number | null
  aov: number | null
  /** Per measure: some row in the segment reported only part of it. */
  partial: { spend: boolean; conversions: boolean; conversionValue: boolean; impressions: boolean }
  /** Per tile: how many distinct creatives carry a mark on any measure the tile reads. */
  partialCreatives: Record<TileKey, number>
}

export type TileKey = 'spend' | 'conversions' | 'roas' | 'aov' | 'impressions'

/**
 * The measures each headline tile reads, its value and its sub line together: a tile is
 * partial exactly when one of these is, and its reason counts the creatives behind them.
 */
export const TILE_MEASURES: Record<TileKey, readonly Measure[]> = {
  spend: ['spend'],
  conversions: ['conversions'],
  roas: ['conversionValue', 'spend', 'conversions'],
  aov: ['conversionValue', 'conversions'],
  impressions: ['impressions', 'spend'],
}

export interface Totals extends ObservedTotals {
  ads: number
  creatives: number
  /** Rows whose sample clears the provisional line, and so can be ranked at all. */
  rankable: number
  /** Rows that cannot: the thin-sample banner's subject. */
  thin: number
  thinSpend: number
}

/**
 * The half of the totals that reads metrics and nothing else, through `aggregateObserved`
 * and `derive` as `totalsOf` always has. It takes no ad counts and no band, because a
 * prior creative carries neither and must not be given them.
 */
export function observedTotalsOf(items: { metrics: ObservedMetrics }[], scopePartial: boolean): ObservedTotals {
  const aggregate = aggregateUnder(items.map((item) => item.metrics), scopePartial)
  const derived = derive(aggregate)
  // Additive zero belongs to an empty segment alone: a segment with creatives whose ads
  // all stayed silent on a measure has no figure for it, and the tiles must show the
  // glyph the comparison's bucket rows show - not a computed zero that fabricates a CPA
  // or an average on top of it. Under a partial scope an empty segment is not a zero
  // either: delivery the scope could not see may belong to it.
  const empty = items.length === 0 && !scopePartial
  // A creative counts on a measure when it says so itself, or when it reported nothing for
  // a measure the segment marks - the mix `aggregateUnder` marks. Distinct per tile, so a
  // creative short of spend and another short of value are two behind the ROAS tile.
  const marks = (item: { metrics: ObservedMetrics }, measure: Measure) =>
    item.metrics.incomplete.includes(measure) ||
    (item.metrics[measure] === null && aggregate.incomplete.includes(measure))
  const partialCreatives = Object.fromEntries(
    (Object.keys(TILE_MEASURES) as TileKey[]).map((tile) => [
      tile,
      items.filter((item) => TILE_MEASURES[tile].some((measure) => marks(item, measure))).length,
    ]),
  ) as Record<TileKey, number>
  return {
    spend: empty ? 0 : aggregate.spend,
    conversions: empty ? 0 : aggregate.conversions,
    impressions: empty ? 0 : aggregate.impressions,
    conversionValue: empty ? 0 : aggregate.conversionValue,
    roas: derived.roas.value,
    aov: derived.aov.value,
    partial: {
      spend: aggregate.incomplete.includes('spend'),
      conversions: aggregate.incomplete.includes('conversions'),
      conversionValue: aggregate.incomplete.includes('conversionValue'),
      impressions: aggregate.incomplete.includes('impressions'),
    },
    partialCreatives,
  }
}

/**
 * What the six tiles and the thin-sample banner describe.
 *
 * Always the segment, never the floored list: the reader's minimums hide rows from a
 * table, and a tile that moved with them would stop answering "how is this account doing"
 * the moment it was asked "show me only the big spenders".
 *
 * The sums and the partial marks run through `aggregateObserved`, the one implementation
 * the hypotheses screen's buckets share, and the ratios are then derived from the
 * nullable aggregate through `derive` - the same reading the comparison's buckets get.
 * The sums stay nullable too: an empty segment totals zero, but a segment with creatives
 * whose ads never reported a measure has no figure for it, so the drill into such a
 * bucket shows what the comparison's bucket row shows - the glyph - and never a
 * fabricated zero or a ratio computed on top of one.
 */
export function totalsOf(segmentRows: Row[], scopePartial: boolean, thresholds: ConfidenceThresholds): Totals {
  const rows = launched(segmentRows)
  const thin = rows.filter((row) => belowFloor(row, thresholds))
  return {
    ...observedTotalsOf(rows, scopePartial),
    ads: rows.reduce((total, row) => total + row.ads.length, 0),
    creatives: rows.length,
    rankable: rows.length - thin.length,
    thin: thin.length,
    thinSpend: sumMoney(thin.map((row) => row.metrics.spend ?? 0)),
  }
}
